import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axiosInstance from "../../axiosInstance";
import { updateDevisEtat } from "../../api/devis";
export const persistEtatDevis = updateDevisEtat;


/* ================= Utils ================= */
export const num = (v, fb = 0) => (Number.isFinite(Number(v)) ? Number(v) : fb);
export const normalizeEtatKey = (v = "") => String(v).toLowerCase().replace(/_/g, " ");
const money = (n) =>
    Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const dateFR = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");

/* groupage par mois */
export const toMonthKey = (iso) => {
    if (!iso) return "0000-00";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "0000-00";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
export const toMonthLabel = (key) => {
    const [y, m] = key.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(d);
};

/* ===== Canonical status <-> labels ===== */
const LABELS = {
    devis: { brouillon: "Brouillon", envoye: "Envoyé", accepte: "Accepté", abandonne: "Abandonné" },
    facture: { brouillon: "Brouillon", envoyee: "Envoyée", payee: "Payée", annulee: "Annulée" },
};
const KEY_BY_LABEL = {
    devis: Object.fromEntries(Object.entries(LABELS.devis).map(([k, v]) => [v, k])),
    facture: Object.fromEntries(Object.entries(LABELS.facture).map(([k, v]) => [v, k])),
};
const statusToLabel = (kind, key) => LABELS[kind]?.[key] ?? key ?? "";
const labelToStatus = (kind, label) => KEY_BY_LABEL[kind]?.[label] ?? null;

/* Fallback depuis anciens champs "etat" */
const legacyToKey = (kind, raw = "") => {
    const s = normalizeEtatKey(raw);
    if (kind === "devis") {
        if (s.includes("accept")) return "accepte";
        if (s.includes("abandon")) return "abandonne";
        if (s.includes("envoy")) return "envoye";
        return "brouillon";
    } else {
        if (s.includes("pay")) return "payee";
        if (s.includes("annul") || s.includes("abandon")) return "annulee";
        if (s.includes("envoy")) return "envoyee";
        return "brouillon";
    }
};

/* Badges + stripe par clé canonique */
const BADGE_BY_KEY = {
    brouillon: "bg-gray-200 text-gray-800",
    envoye: "bg-amber-100 text-amber-700",
    envoyee: "bg-amber-100 text-amber-700",
    accepte: "bg-sky-100 text-sky-700",
    payee: "bg-emerald-100 text-emerald-700",
    abandonne: "bg-red-100 text-red-700",
    annulee: "bg-red-100 text-red-700",
};
const stripeByKey = (k) => {
    if (k === "payee") return "bg-emerald-300";
    if (k === "accepte") return "bg-sky-300";
    if (k === "envoye" || k === "envoyee") return "bg-amber-300";
    return "bg-slate-300";
};

const fmtNum = (kind, raw, id) => {
    const prefix = kind === "facture" ? "FAC-" : "DEV-";
    const s = String(raw ?? "").trim();
    if (s.startsWith(prefix)) return s;
    if (/^[A-Z]{3,}-/.test(s)) return s;
    if (!s && id != null) return `${prefix}${String(id).padStart(4, "0")}`;
    return `${prefix}${s}`;
};

/* ===== Lignes & coûts ===== */
const getLignes = (doc) => {
    if (!doc) return [];
    try {
        if (Array.isArray(doc.contenu)) return doc.contenu;
        if (doc.contenu) return JSON.parse(doc.contenu);
    } catch { }
    try {
        if (Array.isArray(doc.lignes)) return doc.lignes;
        if (doc.lignes) return JSON.parse(doc.lignes);
    } catch { }
    return [];
};
const qtyOf = (l) => num(l.quantite ?? l.qty ?? 1, 1);
const puOf = (l) => num(l.prixUnitaire ?? l.pu ?? l.prix ?? 0, 0);
const costU = (l) => num(l.coutUnitaire ?? l.cout ?? l.pa ?? l.prixAchat ?? 0, 0);

export function totalsWithCost(doc, fallbackTotalHT) {
    const lignes = getLignes(doc);
    const totalHT_fromLines = lignes.reduce((s, l) => s + qtyOf(l) * puOf(l), 0);
    const totalHT =
        num(doc.total_ht ?? doc.montant_ht) ||
        (Number.isFinite(totalHT_fromLines) ? totalHT_fromLines : num(fallbackTotalHT));

    const metaCost = num(doc?.meta?.cout_total);
    const costLines = lignes.reduce((s, l) => s + qtyOf(l) * costU(l), 0);
    const totalCost = Number.isFinite(metaCost) && metaCost > 0 ? metaCost : costLines;

    const marge = Math.max(0, totalHT - totalCost);
    const taux = totalHT > 0 ? marge / totalHT : 0;
    return { totalHT, totalCost, marge, taux };
}

/* Aides PU & TVA% */
const sumQty = (lignes) => lignes.reduce((s, l) => s + qtyOf(l), 0);
export const avgPU = (doc, totalHT) => {
    const q = sumQty(getLignes(doc));
    return q > 0 ? totalHT / q : totalHT;
};
export const tvaRate = (totalHT, totalTTC) =>
    (totalHT > 0 ? Math.max(0, (totalTTC - totalHT) / totalHT) * 100 : 0);

/* Paiements (factures) */
export const getPaid = (f) => {
    const direct = num(f.montant_regle) || num(f.total_regle) || num(f.regle) || 0;
    let fromRegs = 0;
    try {
        const regs = Array.isArray(f.reglements) ? f.reglements : f.reglements ? JSON.parse(f.reglements) : [];
        fromRegs = regs.reduce((s, r) => s + num(r.montant || r.amount || r.somme), 0);
    } catch { }
    return Math.max(direct, fromRegs);
};


export async function persistEtatFacture(id, newLabelOrKey) {
    const key =
        ["brouillon", "envoyee", "payee", "annulee"].includes(String(newLabelOrKey).toLowerCase())
            ? String(newLabelOrKey).toLowerCase()
            : (labelToStatus("facture", newLabelOrKey) || "brouillon");

    const payloadBoth = { status: key, etat: key };
    const payloadEtat = { etat: key };
    const payloadStatus = { status: key };

    try { return (await axiosInstance.patch(`/factures/${id}`, payloadBoth)).data; } catch { }
    try { return (await axiosInstance.patch(`/factures/${id}`, payloadEtat)).data; } catch { }
    try { return (await axiosInstance.patch(`/factures/${id}/etat`, payloadEtat)).data; } catch { }
    try { return (await axiosInstance.put(`/factures/${id}`, payloadBoth)).data; } catch { }
    try { return (await axiosInstance.put(`/factures/${id}`, payloadEtat)).data; } catch { }
    try { return (await axiosInstance.patch(`/factures/${id}/etat`, payloadStatus)).data; } catch { return null; }
}

export async function persistCoutTotal(kind, id, cout_total) {
    const payload = { meta: { cout_total: Number(cout_total) } };
    const base = kind === "devis" ? "devis" : "factures";
    try {
        return (await axiosInstance.patch(`/${base}/${id}`, payload)).data;
    } catch {
        try {
            return (await axiosInstance.put(`/${base}/${id}`, payload)).data;
        } catch {
            return null;
        }
    }
}

/* ================= Header ================= */
export function TitleRow({ title }) {
    return (
        <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-slate-700">{title}</h1>
        </div>
    );
}

/* ================= Filtres ================= */
function useOutsideClose(refs, onClose) {
    useEffect(() => {
        const onDocClick = (e) => {
            const isInside = refs.some((r) => r.current && r.current.contains(e.target));
            if (!isInside) onClose();
        };
        const onKey = (e) => e.key === "Escape" && onClose();
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [refs, onClose]);
}

function FiltersDropdown({ open, onClose, tab, values, setValues, uniqueClients, clientOptions, sort, setSort }) {
    const popRef = useRef(null);
    useOutsideClose([popRef], () => open && onClose());
    if (!open) return null;

    const isDevis = tab === "devis";
    const etatOptions = isDevis
        ? ["(Tous)", ...Object.values(LABELS.devis)]
        : ["(Tous)", ...Object.values(LABELS.facture)];
    const clientsList = Array.isArray(uniqueClients)
        ? uniqueClients
        : (Array.isArray(clientOptions) ? clientOptions : []);


    return (
        <div ref={popRef} className="absolute z-[3000] mt-2 w-[min(880px,92vw)] rounded-xl border bg-white shadow-2xl p-0">
            {/* ── En-tête tri (collé en haut) ───────────────────────────── */}
            <div className="sticky top-0 z-10 bg-white/90 backdrop-blur px-4 py-3 border-b">
                <div className="text-[11px] font-semibold text-slate-600 mb-2">Trier par</div>
                <div className="flex flex-wrap gap-1">
                    {[
                        ["date", "Date"],
                        ["numero", "Numéro"],
                        ["cost", "Coût HT"],
                        ["marge", "Marge %€"],
                        ["pu", "PU HT"],
                        ["ttc", "Prix TTC"],
                    ].map(([key, label]) => (
                        <button
                            key={key}
                            onClick={() =>
                                setSort((s) => ({
                                    key,
                                    dir: s.key === key ? (s.dir === "asc" ? "desc" : "asc") : "asc",
                                }))
                            }
                            className="text-xs px-2 py-1 rounded border hover:bg-gray-50"
                            title="Trier"
                        >
                            {label} {sort?.key === key ? (sort?.dir === "asc" ? "↑" : "↓") : ""}
                        </button>
                    ))}
                    <span className="ml-2 text-[11px] text-slate-500">
                        {sort?.dir === "asc" ? "croissant" : "décroissant"}
                    </span>
                </div>
            </div>

            {/* Corps du panneau */}
            <div className="p-4 space-y-3">
                <div>
                    <div className="text-xs font-semibold text-slate-600 mb-1">Statut</div>
                    <select
                        className="w-full border rounded px-2 py-1 text-sm"
                        value={values.etat || ""}
                        onChange={(e) => setValues((v) => ({ ...v, etat: e.target.value || "" }))}
                    >
                        {etatOptions.map((opt) => (
                            <option key={opt} value={opt === "(Tous)" ? "" : opt}>{opt}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <div className="text-xs font-semibold text-slate-600 mb-1">Client</div>
                    <input
                        list="clients-centre"
                        placeholder="Tape un nom…"
                        className="w-full border rounded px-2 py-1 text-sm"
                        value={values.client || ""}
                        onChange={(e) => setValues((v) => ({ ...v, client: e.target.value }))}
                    />
                    <datalist id="clients-centre">
                        {clientsList.slice(0, 80).map((c) => <option key={c} value={c} />)}
                    </datalist>
                </div>

                <div>
                    <div className="text-xs font-semibold text-slate-600 mb-1">Période</div>
                    <div className="flex items-center gap-2">
                        <input
                            type="date"
                            className="border rounded px-2 py-1 text-sm w-full"
                            value={values.from || ""}
                            onChange={(e) => setValues((v) => ({ ...v, from: e.target.value }))}
                        />
                        <span className="text-slate-400">→</span>
                        <input
                            type="date"
                            className="border rounded px-2 py-1 text-sm w-full"
                            value={values.to || ""}
                            onChange={(e) => setValues((v) => ({ ...v, to: e.target.value }))}
                        />
                    </div>
                </div>

                {tab !== "devis" && (
                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={!!values.onlyPaid}
                            onChange={(e) => setValues((v) => ({ ...v, onlyPaid: e.target.checked }))}
                        />
                        Uniquement les factures payées
                    </label>
                )}

                <div className="text-xs font-semibold text-slate-600">Total HT (min / max)</div>
                <div className="flex items-center gap-2">
                    <input
                        type="number" step="0.01"
                        className="border rounded px-2 py-1 text-sm w-full"
                        placeholder="min"
                        value={values.minHT ?? ""}
                        onChange={(e) => setValues((v) => ({ ...v, minHT: e.target.value }))}
                    />
                    <input
                        type="number" step="0.01"
                        className="border rounded px-2 py-1 text-sm w-full"
                        placeholder="max"
                        value={values.maxHT ?? ""}
                        onChange={(e) => setValues((v) => ({ ...v, maxHT: e.target.value }))}
                    />
                </div>
                {/* … tes contrôles Statut / Client / Période / HT min-max … */}
                <div className="pt-2 flex items-center justify-end gap-2">
                    <button
                        className={btnWhiteOutline}
                        onClick={() => setValues({ etat: "", client: "", from: "", to: "", minHT: "", maxHT: "", onlyPaid: false })}
                    >
                        Réinitialiser
                    </button>
                    <button className={btnSolid} onClick={onClose}>Fermer</button>
                </div>
            </div>
        </div>
    );
}

/* ====== styles “identiques Documents” ====== */
const BTN_BASE =
    "inline-flex items-center justify-center h-9 px-4 rounded-full text-sm font-medium leading-none transition focus:outline-none focus:ring-2 focus:ring-sky-300";
const btnGhost = `${BTN_BASE} bg-gray-200 hover:bg-gray-300 text-slate-700`;      // = ancien “gris”
const btnSolid = `${BTN_BASE} bg-sky-700 hover:bg-sky-600 text-white`;             // = ancien “bleu”
const btnWhiteOutline = `${BTN_BASE} bg-white border border-slate-300 text-slate-700 hover:bg-gray-50`;
const btnBlue = btnSolid;
/* ====== ToolbarRow (remplace le tien) ====== */
export function ToolbarRow({
    q, setQ, onExport, onCreateDevis, onCreateFacture,
    filters, setFilters, clientOptions, sort, setSort, tab
}) {
    const [open, setOpen] = useState(false);
    const leftRef = useRef(null);
    const btnRef = useRef(null);

    useOutsideClose([leftRef, btnRef], () => setOpen(false));

    return (
        <div className="rounded-lg border bg-white px-3 py-2">
            <div className="grid grid-cols-3 items-center gap-3">
                {/* GAUCHE : Filtres + Recherche (collés) */}
                <div className="justify-self-start relative" ref={leftRef}>
                    <div className="inline-flex items-stretch h-9">
                        <button
                            ref={btnRef}
                            onClick={() => setOpen(v => !v)}
                            aria-expanded={open}
                            aria-controls="dropdown-filtres-docs"
                            className={btnWhiteOutline + " rounded-r-none"}
                        >
                            Filtres
                        </button>
                        <input
                            className="w-[min(360px,80vw)] px-3 text-sm rounded-r-full border border-l-0 border-slate-300 bg-gray-100
                         placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent"
                            placeholder="Rechercher un devis ou une facture…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            aria-label="Rechercher un document"
                            style={{ marginLeft: "-1px" }}
                        />
                    </div>

                    {/* Dropdown filtres : z-index fort pour passer au-dessus des tables */}
                    <div id="dropdown-filtres-docs" className="absolute left-0 top-full z-[3000]">
                        <FiltersDropdown
                            tab={tab}
                            open={open}
                            onClose={() => setOpen(false)}
                            values={filters}
                            setValues={setFilters}
                            clientOptions={clientOptions}
                            sort={sort}
                            setSort={setSort}
                        />
                    </div>
                </div>

                {/* CENTRE : actions grises (identiques) */}
                <div className="justify-self-center mx-auto flex items-center gap-2">
                    <button className={btnGhost} onClick={onCreateDevis}>Nouveau devis</button>
                    <button className={btnGhost} onClick={onCreateFacture}>Nouvelle facture</button>
                </div>

                {/* DROITE : Exporter */}
                <div className="justify-self-end">
                    <button className={btnSolid} onClick={onExport} aria-label="Exporter les documents">Exporter…</button>
                </div>
            </div>
        </div>
    );
}

/* ================= Etat badge inline ================= */
const MENU_BTN_BASE =
    "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border border-transparent focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-1";

function EtatBadgeEditable({ kind, value, onChange }) {
    // value est le LIBELLÉ courant (ex: "Envoyée"); on le convertit vers clé pour styles
    const key = labelToStatus(kind, value) ?? legacyToKey(kind, value);
    const cls = BADGE_BY_KEY[key] || BADGE_BY_KEY.brouillon;

    const [open, setOpen] = useState(false);
    const btnRef = useRef(null);
    const menuRef = useRef(null);

    // Coordonnées du menu (en position: fixed)
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    const options =
        kind === "devis" ? Object.values(LABELS.devis) : Object.values(LABELS.facture);

    // Calcule et met à jour la position du menu
    const updateCoords = () => {
        if (!btnRef.current) return;
        const r = btnRef.current.getBoundingClientRect();
        const MENU_W = 176; // w-44 = 11rem = 176px avec base 16px
        const margin = 6;

        // aligné à droite du bouton, mais sans sortir de l’écran
        const left = Math.max(
            8,
            Math.min(window.innerWidth - 8 - MENU_W, r.right - MENU_W)
        );
        const top = Math.max(8, Math.min(window.innerHeight - 8, r.bottom + margin));
        setCoords({ top, left });
    };

    useEffect(() => {
        if (!open) return;
        updateCoords();
        const onScrollOrResize = () => updateCoords();
        window.addEventListener("scroll", onScrollOrResize, true);
        window.addEventListener("resize", onScrollOrResize, true);
        return () => {
            window.removeEventListener("scroll", onScrollOrResize, true);
            window.removeEventListener("resize", onScrollOrResize, true);
        };
    }, [open]);

    // Fermer au clic extérieur / Échap
    useEffect(() => {
        const onDocClick = (e) => {
            if (!open) return;
            const isBtn = btnRef.current && btnRef.current.contains(e.target);
            const isMenu = menuRef.current && menuRef.current.contains(e.target);
            if (!isBtn && !isMenu) setOpen(false);
        };
        const onKey = (e) => open && e.key === "Escape" && setOpen(false);
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    return (
        <>
            <button
                ref={btnRef}
                className={`${MENU_BTN_BASE} ${cls} px-2 py-0`}
                onClick={() => setOpen((v) => !v)}
            >
                {value}
                <span className="ml-1 text-gray-500">▾</span>
            </button>

            {open &&
                createPortal(
                    <div
                        ref={menuRef}
                        role="menu"
                        className="fixed z-[4000] w-44 bg-white border rounded shadow text-xs py-1"
                        style={{ top: coords.top, left: coords.left }}
                    >
                        {options.map((opt) => (
                            <button
                                key={opt}
                                role="menuitem"
                                className="w-full text-left px-3 py-2 hover:bg-gray-50"
                                onClick={() => {
                                    setOpen(false);
                                    onChange?.(opt);
                                }}
                            >
                                {opt}
                            </button>
                        ))}
                    </div>,
                    document.body
                )}
        </>
    );
}

/* ================= Tableau ================= */
function Th({ children, className = "" }) {
    return <th className={`px-3 py-2 text-[12.5px] font-semibold text-slate-700 ${className}`}>{children}</th>;
}
function Td({ children, className = "", onClick }) {
    return <td onClick={onClick} className={`px-3 py-2 ${className}`}>{children}</td>;
}
function GroupSummary({ rows, kind }) {
    const n = rows.length;
    const sum = rows.reduce(
        (acc, x) => {
            const totalTTC = num(x.total_ttc ?? x.montant_ttc ?? x.total);
            const { totalHT, totalCost, marge } = totalsWithCost(x, num(x.total_ht ?? x.montant_ht));
            acc.ht += totalHT;
            acc.ttc += totalTTC;
            acc.cost += totalCost;
            acc.marge += Math.max(0, marge);
            return acc;
        },
        { ht: 0, ttc: 0, cost: 0, marge: 0 }
    );
    const taux = sum.ht > 0 ? sum.marge / sum.ht : 0;
    const tvaTotale = Math.max(0, sum.ttc - sum.ht);

    return (
        <tr className="bg-slate-100 border-t text-slate-700">
            <Td />
            <Td />
            <Td className="text-base font-semibold" colSpan={3}>
                Totaux du mois — {n} document{n > 1 ? "s" : ""}
            </Td>
            <Td className="text-right font-medium">{money(sum.cost)}</Td>
            <Td className="text-right font-medium">
                <div className="flex flex-col items-end leading-tight">
                    <span>{money(sum.marge)}</span>
                    <span className="text-[11px] text-slate-500">moy {(taux * 100).toFixed(1)}%</span>
                </div>
            </Td>
            <Td className="text-right font-medium" title={`TVA totale : ${money(tvaTotale)}`}>
                {(tvaTotale / (sum.ht || 1) * 100).toFixed(1)} %
            </Td>
            <Td className="text-right font-medium">{money(sum.ttc)}</Td>
            <Td />
        </tr>
    );
}
function GroupSummaryRow({ rows, kind }) {
    // Σ sur le groupe affiché
    const sums = rows.reduce(
        (acc, x) => {
            const totalTTC = num(x.total_ttc ?? x.montant_ttc ?? x.total);
            const { totalHT, totalCost, marge } = totalsWithCost(x, num(x.total_ht ?? x.montant_ht));
            const lignes = (() => {
                try {
                    if (Array.isArray(x.contenu)) return x.contenu;
                    if (x.contenu) return JSON.parse(x.contenu);
                } catch { }
                return [];
            })();
            const qte = lignes.reduce((s, l) => s + (Number(l.quantite ?? l.qty ?? 1) || 0), 0);

            acc.ht += totalHT;
            acc.ttc += totalTTC;
            acc.cost += totalCost;
            acc.marge += Math.max(0, marge);
            acc.qte += qte;
            return acc;
        },
        { ht: 0, ttc: 0, cost: 0, marge: 0, qte: 0 }
    );

    const taux = sums.ht > 0 ? sums.marge / sums.ht : 0;
    const tvaTotale = Math.max(0, sums.ttc - sums.ht);
    const tvaMoy = sums.ht > 0 ? (tvaTotale / sums.ht) * 100 : 0;
    const puMoy = sums.qte > 0 ? sums.ht / sums.qte : 0;

    // NB : le tableau a 11 colonnes (stripe + 4 infos + 5 chiffres + statut)
    return (
        <tr className="bg-slate-100 border-t text-slate-700">
            {/* 1) stripe */}
            <td className="w-[6px] px-3 py-2" />
            {/* 2) Date (vide) */}
            <td className="px-3 py-2 text-xs" />
            {/* 3-5) libellé sur 3 colonnes (Numéro + Client + Adresse) */}
            <td colSpan={3} className="px-3 py-2 text-sm font-semibold">
                Totaux du mois — {rows.length} document{rows.length > 1 ? "s" : ""}
            </td>
            {/* 6) Coût HT */}
            <td className="px-3 py-2 text-right font-medium">{(sums.ht > 0) ? (Number(sums.cost || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €") : "—"}</td>
            {/* 7) Marge (€ + taux moyen) */}
            <td className="px-3 py-2 text-right font-medium">
                <div className="flex flex-col items-end leading-tight">
                    <span>{(Number(sums.marge || 0)).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
                    <span className="text-[11px] text-slate-500">moy {(taux * 100).toFixed(1)}%</span>
                </div>
            </td>
            {/* 8) PU HT moyen */}
            <td className="px-3 py-2 text-right font-medium">{(puMoy || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</td>
            {/* 9) %TVA moyen */}
            <td className="px-3 py-2 text-right font-medium">{sums.ht > 0 ? `${tvaMoy.toFixed(1)} %` : "—"}</td>
            {/* 10) Prix TTC total */}
            <td className="px-3 py-2 text-right font-medium">{(Number(sums.ttc || 0)).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</td>
            {/* 11) Statut (vide) */}
            <td className="px-3 py-2" />
        </tr>
    );
}

export function TableDocs({ kind, rows, onRowClick, onEtatChange, onOpenCost }) {
    const [marginView, setMarginView] = useState("pct"); // 'pct' | 'eur'
    return (
        <div className="overflow-x-auto overflow-y-visible rounded-lg border bg-white">             <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
                <tr>
                    <Th><span className="sr-only">•</span></Th>
                    <Th>Date</Th>
                    <Th>Numéro</Th>
                    <Th>Client</Th>
                    <Th>Adresse</Th>
                    <Th className="text-right">Coût HT</Th>
                    <Th className="text-right">
                        <div className="flex items-center justify-end gap-2">
                            <span className="font-semibold text-slate-700">Marge</span>
                            <div className="inline-flex items-center rounded-full border border-slate-300 overflow-hidden">
                                <button
                                    type="button"
                                    onClick={() => setMarginView("pct")}
                                    className={`px-2 py-[2px] text-xs ${marginView === "pct" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}
                                    aria-pressed={marginView === "pct"}
                                >
                                    %
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setMarginView("eur")}
                                    className={`px-2 py-[2px] text-xs ${marginView === "eur" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}
                                    aria-pressed={marginView === "eur"}
                                >
                                    €
                                </button>
                            </div>
                        </div>
                    </Th>
                    <Th className="text-right">PU HT</Th>
                    <Th className="text-right">TVA %</Th>
                    <Th className="text-right">Prix TTC</Th>
                    <Th className="text-right">Statut</Th>
                </tr>
            </thead>


            <tbody>
                {rows.length === 0 && (
                    <tr>
                        <td colSpan={11} className="text-center py-8 text-gray-500">Aucun document.</td>
                    </tr>
                )}

                {rows.map((doc) => {
                    const isFacture = kind === "facture";
                    const numero = isFacture
                        ? fmtNum("facture", doc.numero || doc.numero_facture, doc.id)
                        : fmtNum("devis", doc.numero_devis, doc.id);
                    const date = isFacture ? (doc.date || doc.date_facture || doc.created_at) : (doc.date_devis || doc.created_at);
                    const clientNom = isFacture ? (doc.client_nom || doc.client?.nom) : doc.client_nom;
                    const clientAdr = isFacture ? (doc.client_adresse || doc.client?.adresse) : doc.client_adresse;

                    const totalTTC = num(doc.total_ttc ?? doc.montant_ttc ?? doc.total);
                    const { totalHT, totalCost, marge, taux } = totalsWithCost(doc, num(doc.total_ht ?? doc.montant_ht));
                    const pu = avgPU(doc, totalHT);
                    const tvaPct = tvaRate(totalHT, totalTTC);

                    // valeur affichée (libellé)
                    const currentKey = isFacture ? (doc.status || legacyToKey("facture", doc.etat)) : (doc.status || legacyToKey("devis", doc.etat));
                    const currentLabel = statusToLabel(isFacture ? "facture" : "devis", currentKey);

                    const stripeCls = stripeByKey(currentKey);

                    return (
                        <tr key={`${kind}-${doc.id}`} className="border-t hover:bg-gray-50">
                            <Td className="w-[6px]"><span className={`block w-[6px] h-5 rounded-sm ${stripeCls}`} aria-hidden /></Td>

                            <Td onClick={() => onRowClick?.(doc)} className="cursor-pointer">{dateFR(date)}</Td>
                            <Td onClick={() => onRowClick?.(doc)} className="cursor-pointer font-medium">{numero}</Td>
                            <Td onClick={() => onRowClick?.(doc)} className="cursor-pointer max-w-[32ch] truncate" title={clientNom || "—"}>
                                {clientNom || "—"}
                            </Td>
                            <Td onClick={() => onRowClick?.(doc)} className="cursor-pointer max-w-[44ch] truncate" title={clientAdr || "—"}>
                                {clientAdr || "—"}
                            </Td>
                            <Td className="text-right"
                            /* Coût HT (ouvre la popup) */
                            >
                                <button
                                    className="underline decoration-dotted hover:decoration-solid"
                                    onClick={() => onOpenCost?.(doc)}
                                    title="Modifier le coût / la marge"
                                >
                                    {money(totalHT > 0 ? totalCost : 0)}
                                </button>
                            </Td>

                            {/* Marge dynamique */}
                            <Td className="text-right">
                                <button
                                    className="underline decoration-dotted hover:decoration-solid"
                                    onClick={() => onOpenCost?.(doc)}
                                    title="Modifier le coût / la marge"
                                >
                                    {totalHT > 0 ? (marginView === "pct" ? `${(taux * 100).toFixed(1)} %` : money(Math.max(0, marge))) : "—"}
                                </button>
                            </Td>

                            <Td onClick={() => onRowClick?.(doc)} className="cursor-pointer text-right">{money(pu)}</Td>
                            <Td onClick={() => onRowClick?.(doc)} className="cursor-pointer text-right">{totalHT > 0 ? `${tvaPct.toFixed(1)} %` : "—"}</Td>
                            <Td onClick={() => onRowClick?.(doc)} className="cursor-pointer text-right">{money(totalTTC)}</Td>

                            <Td className="text-right">
                                <EtatBadgeEditable
                                    kind={isFacture ? "facture" : "devis"}
                                    value={currentLabel}
                                    onChange={(newLabel) => onEtatChange?.(doc.id, newLabel)}
                                />
                            </Td>
                        </tr>
                    );
                })}
                {rows.length > 0 && <GroupSummaryRow rows={rows} kind={kind} />}            </tbody>
        </table>
        </div>
    );
}

/* ================= Modale Coût & Marge ================= */
export function CostMarginModal({ open, onClose, kind, doc, onSaved }) {
    const { totalHT } = totalsWithCost(doc, num(doc.total_ht ?? doc.montant_ht));
    const [cost, setCost] = useState(() => {
        const { totalCost } = totalsWithCost(doc, num(doc.total_ht ?? doc.montant_ht));
        return totalCost || 0;
    });

    useEffect(() => {
        if (open) {
            const { totalCost } = totalsWithCost(doc, num(doc.total_ht ?? doc.montant_ht));
            setCost(totalCost || 0);
        }
    }, [open, doc]);

    if (!open) return null;

    const margin = Math.max(0, totalHT - cost);
    const marginRate = totalHT > 0 ? (margin / totalHT) * 100 : 0;

    const setFromMargin = (m) => {
        const target = Math.max(0, Number(m) || 0);
        const nextCost = Math.max(0, totalHT - target);
        setCost(nextCost);
    };
    const setFromRate = (r) => {
        const rate = Math.max(0, Math.min(100, Number(r) || 0));
        const target = (rate / 100) * totalHT;
        const nextCost = Math.max(0, totalHT - target);
        setCost(nextCost);
    };

    const save = async () => {
        const saved = await persistCoutTotal(kind, doc.id, cost);
        if (!saved) { alert("❌ Impossible d’enregistrer le coût."); return; }
        onSaved?.(Number(cost));
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[2000] grid place-items-center bg-black/40">
            <div className="bg-white rounded-xl shadow-2xl w-[560px] max-w-[95vw]">
                <div className="px-5 py-3 border-b flex items-center justify-between">
                    <h3 className="text-lg font-semibold">
                        Coût & Marge — {kind === "devis" ? "Devis" : "Facture"}{" "}
                        {fmtNum(kind, kind === "devis" ? doc.numero_devis : doc.numero || doc.numero_facture, doc.id)}
                    </h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-black">✕</button>
                </div>

                <div className="p-5 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="p-3 rounded border bg-gray-50">
                            <div className="text-sm text-slate-600">Total HT</div>
                            <div className="text-lg font-semibold">{money(totalHT)}</div>
                        </div>

                        <div className="p-3 rounded border">
                            <label className="text-sm text-slate-600">Coût HT</label>
                            <input
                                type="number" step="0.01"
                                className="w-full border rounded px-2 py-1 mt-1"
                                value={cost}
                                onChange={(e) => setCost(Math.max(0, Number(e.target.value) || 0))}
                            />
                        </div>

                        <div className="p-3 rounded border">
                            <label className="text-sm text-slate-600">Marge (€)</label>
                            <input
                                type="number" step="0.01"
                                className="w-full border rounded px-2 py-1 mt-1"
                                value={margin}
                                onChange={(e) => setFromMargin(e.target.value)}
                            />
                        </div>

                        <div className="p-3 rounded border">
                            <label className="text-sm text-slate-600">Marge (%)</label>
                            <input
                                type="number" step="0.1"
                                className="w-full border rounded px-2 py-1 mt-1"
                                value={marginRate.toFixed(1)}
                                onChange={(e) => setFromRate(e.target.value)}
                            />
                        </div>
                    </div>
                    <p className="text-xs text-slate-500">
                        Astuce : modifie <strong>un</strong> champ (Coût, Marge €, ou Marge %) et les autres se recalculent automatiquement.
                    </p>
                </div>

                <div className="px-5 py-3 border-t flex items-center justify-end gap-2">
                    <button onClick={onClose} className="inline-flex items-center rounded-full border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition">
                        Annuler
                    </button>
                    <button onClick={save} className="inline-flex items-center rounded-full bg-sky-700 px-4 py-2 text-sm text-white shadow hover:bg-sky-600 transition">
                        Enregistrer
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ================= Export (CSV/JSON) ================= */
const dl = (filename, mime, content) => {
    const blob = new Blob([content], { type: mime });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 0);
};
const escapeCsv = (v) => {
    if (v == null) return "";
    const s = String(v);
    if (/[";,\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
};
const toCsv = (rows, headers) => {
    const head = headers.map((h) => escapeCsv(h.label)).join(";");
    const body = rows.map((r) => headers.map((h) => escapeCsv(r[h.key])).join(";")).join("\n");
    return head + "\n" + body;
};

export function ExportModal({ open, onClose, tab, devis, factures }) {
    const [fmt, setFmt] = useState("csv");
    if (!open) return null;

    const headers = [
        { key: "date", label: "Date" },
        { key: "numero", label: "Numero" },
        { key: "client", label: "Client" },
        { key: "adresse", label: "Adresse" },
        { key: "ht", label: "Total_HT" },
        { key: "tva", label: "TVA" },
        { key: "ttc", label: "Total_TTC" },
        { key: "cout", label: "Cout_HT" },
        { key: "marge", label: "Marge_EUR" },
        { key: "taux", label: "Marge_%" },
        { key: "etat", label: "Etat" },
    ];

    const mapRow = (x, kind) => {
        const totalTTC = num(x.total_ttc ?? x.montant_ttc ?? x.total ?? 0);
        const { totalHT, totalCost, marge, taux } = totalsWithCost(x, num(x.total_ht ?? x.montant_ht));
        const tva = Math.max(0, totalTTC - totalHT);

        const currentKey = kind === "devis" ? (x.status || legacyToKey("devis", x.etat)) : (x.status || legacyToKey("facture", x.etat));
        const currentLabel = statusToLabel(kind, currentKey);

        return {
            date: dateFR(kind === "devis" ? x.date_devis || x.created_at : x.date || x.date_facture || x.created_at),
            numero: fmtNum(kind, kind === "devis" ? x.numero_devis : x.numero || x.numero_facture, x.id),
            client: kind === "devis" ? x.client_nom || "" : x.client_nom || x.client?.nom || "",
            adresse: kind === "devis" ? x.client_adresse || "" : x.client_adresse || x.client?.adresse || "",
            ht: totalHT,
            tva,
            ttc: totalTTC,
            cout: totalCost,
            marge,
            taux: (taux * 100).toFixed(1),
            etat: currentLabel,
        };
    };

    const build = () => {
        if (fmt === "json") {
            const payload = {
                exported_at: new Date().toISOString(),
                tab,
                devis: devis.map((d) => mapRow(d, "devis")),
                factures: factures.map((f) => mapRow(f, "facture")),
            };
            dl(`export-${tab}-${Date.now()}.json`, "application/json;charset=utf-8", JSON.stringify(payload, null, 2));
            onClose();
            return;
        }
        const rows =
            tab === "devis"
                ? devis.map((d) => mapRow(d, "devis"))
                : tab === "factures"
                    ? factures.map((f) => mapRow(f, "facture"))
                    : [...devis.map((d) => mapRow(d, "devis")), ...factures.map((f) => mapRow(f, "facture"))];

        dl(`export-${tab}-${Date.now()}.csv`, "text/csv;charset=utf-8", toCsv(rows, headers));
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[2000] grid place-items-center bg-black/40">
            <div className="bg-white rounded-xl shadow-2xl w-[560px] max-w-[95vw]">
                <div className="px-5 py-3 border-b flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Exporter</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-black">✕</button>
                </div>
                <div className="p-5 space-y-4">
                    <div className="text-sm">Format</div>
                    <div className="flex gap-4">
                        <label className="inline-flex items-center gap-2">
                            <input type="radio" checked={fmt === "csv"} onChange={() => setFmt("csv")} /> CSV (Excel)
                        </label>
                        <label className="inline-flex items-center gap-2">
                            <input type="radio" checked={fmt === "json"} onChange={() => setFmt("json")} /> JSON
                        </label>
                    </div>
                </div>
                <div className="px-5 py-3 border-t flex items-center justify-end gap-2">
                    <button onClick={onClose} className="inline-flex items-center rounded-full border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition">
                        Annuler
                    </button>
                    <button onClick={build} className="inline-flex items-center rounded-full bg-sky-700 px-4 py-2 text-sm text-white shadow hover:bg-sky-600 transition">
                        Exporter
                    </button>
                </div>
            </div>
        </div>
    );
}
